/* eslint-disable no-plusplus */
import React, {
  createContext,
  useState,
  useRef,
  useLayoutEffect,
  useContext,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { ReactComponent as SuccessIcon } from './icons/success.svg';
import { ReactComponent as WarningIcon } from './icons/warning.svg';
import { ReactComponent as AlertIcon } from './icons/alert.svg';
import { ReactComponent as InfoIcon } from './icons/info.svg';
import { ReactComponent as CloseIcon } from './icons/close.svg';

import styles from './style.cssmodule.css';

/* eslint-disable */
const getStatusIcon = (toastType) =>  toastType === 'info'    ? <InfoIcon/> :
                                      toastType === 'warning' ? <WarningIcon/> :
                                      toastType === 'alert'   ? <AlertIcon/> :
                                      toastType === 'success' ? <SuccessIcon/> :
                                                                <InfoIcon/>;
  /* eslint-enable */

export const ToastContext = createContext();

export const useToast = () => useContext(ToastContext);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const toastRefs = useRef(new Map());
  const animationCounts = useRef(new Map());

  const { formatMessage: t } = useIntl();

  const generateToastId = () => `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const showToast = useCallback(({
    message,
    type = 'info',
    toastDuration = 5000,
    transitionDuration = 1000,
  }) => {
    document.documentElement.style.setProperty('--toast-duration', `${toastDuration - transitionDuration}ms`);
    document.documentElement.style.setProperty('--toast-transition-duration', `${transitionDuration}ms`);

    const icon = getStatusIcon(type);
    const toastId = generateToastId();

    const newToast = {
      id: toastId,
      message,
      type,
      icon,
      toastDuration,
      transitionDuration,
    };

    setToasts((prevToasts) => [...prevToasts, newToast]);

    // Auto-remove toast after duration
    setTimeout(() => {
      // eslint-disable-next-line no-use-before-define
      removeToast(toastId);
    }, toastDuration);
  });

  const removeToast = useCallback((toastId) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== toastId));
    toastRefs.current.delete(toastId);
    animationCounts.current.delete(toastId);
  }, []);

  useLayoutEffect(() => {
    // Trigger animation for the latest toast only
    if (toasts.length > 0) {
      const latestToast = toasts[toasts.length - 1];
      const toastElement = toastRefs.current.get(latestToast.id);

      if (toastElement && !toastElement.classList.contains('toast-animate')) {
        toastElement.classList.add('toast-animate');
      }
    }
  }, [toasts]);

  const removeToastAfterAnimation = (e, toastId) => {
    const isAnimatedToast = /toast-animate/.test(e.target.className);
    if (!isAnimatedToast) return;

    const currentCount = animationCounts.current.get(toastId) || 0;
    // eslint-disable-next-line no-plusplus
    animationCounts.current.set(toastId, currentCount + 1);

    const toastFinishedEndAnimation = currentCount + 1 === 2;
    if (toastFinishedEndAnimation) {
      animationCounts.current.delete(toastId);
      removeToast(toastId);
    }
  };

  return (
    <ToastContext.Provider value={{ showToast, toasts }}>
      {children}
      <div className={styles.toastContainer}>
        {toasts.map((toast, index) => (
          <div
            key={toast.id}
            className={styles.toast}
            data-type={toast.type}
            ref={(el) => {
              if (el) {
                toastRefs.current.set(toast.id, el);
              }
            }}
            onAnimationEnd={(e) => removeToastAfterAnimation(e, toast.id)}
            style={{
              top: `${index * 80}px`,
              zIndex: 9999 + index,
            }}
          >
            <div>
              {toast.icon}
              <span>{toast.message}</span>
            </div>
            <button
              type="button"
              onClick={() => removeToast(toast.id)}
              className={styles.closeButton}
              aria-label={t({ id: 'toast.close' })}
            >
              <CloseIcon />
            </button>
            <div className={styles.jauge} style={{ backgroundColor: toast.color }} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

ToastProvider.propTypes = {
  children: PropTypes.node.isRequired,
};



// WEBPACK FOOTER //
// ./components/toast/Toast.jsx